import './style/salesDashboard.scss';
import {AwesomeIcon, ChangeFunction} from '@dvrd/dvr-controls';
import IDate from '@dvrd/idate';
import React, {MouseEventHandler, useContext, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import Account from '../../models/accountModel';
import {PeriodLabel, SalesCategory, SalesColumn, StatCardDataType} from '../../util/enums';
import {
    BluemateControlColor,
    GeneralSalesProduct,
    PortalSelectGroup,
    PortalSelectOption,
    SalesDashboardFilterGroup,
    SalesDashboardStats
} from '../../util/types';
import {BluemateButton, BluematePeriodSelector, BluemateSearchInput, BluemateStatCard} from '../controls';
import BluemateToggleButton from '../controls/button/bluemateToggleButton';
import {AppContext} from '../misc/appContext';
import SalesDashboardTable from './salesDashboardTable';
import PortalSelectController from '../controls/portalSelect/portalSelectController';
import {StatCardData} from '../../util/interfaces.ts';

interface Props {
    onChangeSearch: (value: string) => void;
    onEnterSearch: () => void;
    onChangeCategoryVisibility: (category: SalesCategory) => (toggled: boolean) => void;
    onChangeColumnVisibility: (
        value: SalesColumn | null,
        selected: boolean,
        visibleColumns: Array<SalesColumn>
    ) => void;
    onExport: (exportType: 'excel' | 'csv') => MouseEventHandler;
    onChangeCountries: (countryCodes: string) => void;
    onChangeAccounts: (accountID: string | null, selected: boolean, accountIDs: Array<string>) => void;
    onChangePeriod: (range: [IDate, IDate], presetLabel: PeriodLabel | null) => void;
    onChangeOrder: (order: SalesColumn) => MouseEventHandler;
    onClearFilters: MouseEventHandler;
    onApplyFilter: (filterGroup: SalesDashboardFilterGroup) => void;
    onChangePage: (nextPage: number) => MouseEventHandler;
    onChangeLimit: ChangeFunction<number>;
    onClickProduct: (product: GeneralSalesProduct | null) => MouseEventHandler;
    onUploadCosts: MouseEventHandler;
    onEditCosts: (product: GeneralSalesProduct) => void;
    search: string;
    countryCodes: string;
    accountIDs: Set<string>;
    period: PeriodLabel;
    customPeriodStart: IDate | null;
    customPeriodEnd: IDate | null;
    page: number;
    limit: number;
    total: number;
    order: SalesColumn;
    filters: Array<SalesDashboardFilterGroup>;
    products: Array<GeneralSalesProduct>;
    loadingProducts: boolean;
    loadingStats: boolean;
    stats: SalesDashboardStats | null;
    comparingStats: SalesDashboardStats | null;
    visibleColumns: Set<SalesColumn>;
    comparisonVisible: boolean;
}

const flag = (code: string) => <span className={`fi fi-${code}`} style={{margin: '0 0.375rem'}} />;
const countryItems: Array<PortalSelectOption> = [
    {value: 'NL', label: <>{flag('nl')} NL</>},
    {value: 'BE', label: <>{flag('be')} BE</>},
    {
        value: 'NL,BE',
        label: (
            <>
                {flag('nl')} & {flag('be')}
            </>
        )
    }
];

const visibilityItem = (label: string, letter: string, color: string) => (
    <label className='sales-dashboard-column-select-item'>
        {label} <span className={`column-category ${color}`}>{letter}</span>
    </label>
);

export default function SalesDashboard(props: Props) {
    const {t} = useTranslation();
    const {accounts} = useContext(AppContext).accountContext;
    const columnVisibilityItems: Array<PortalSelectGroup<SalesColumn>> = [
        {
            groupLabel: t('salesDashboardV2.table.sales', 'Verkopen'),
            items: [
                {
                    value: SalesColumn.QUANTITY,
                    label: visibilityItem(t('salesDashboardV2.table.quantitySold', 'Aantal verkocht'), 'S', 'green')
                },
                {
                    value: SalesColumn.REVENUE,
                    label: visibilityItem(t('salesDashboardV2.table.revenue', 'Omzet'), 'S', 'green')
                },
                {
                    value: SalesColumn.ORGANIC_SALES,
                    label: visibilityItem(t('salesDashboardV2.table.organicRevenue', 'Organische omzet'), 'S', 'green')
                }
            ]
        },
        {
            groupLabel: t('salesDashboardV2.table.profit', 'Winst'),
            items: [
                {
                    value: SalesColumn.PROFIT,
                    label: visibilityItem(t('salesDashboardV2.table.profit', 'Winst'), 'W', 'orange')
                },
                {
                    value: SalesColumn.MARGIN,
                    label: visibilityItem(t('salesDashboardV2.table.marginPct', 'Marge %'), 'W', 'orange')
                }
            ]
        },
        {
            groupLabel: t('salesDashboardV2.table.costs', 'Kosten'),
            items: [
                {
                    value: SalesColumn.PROD_COSTS,
                    label: visibilityItem(t('salesDashboardV2.table.productCosts', 'Productkosten'), 'P', 'blue')
                },
                {
                    value: SalesColumn.COMMISSION,
                    label: visibilityItem(t('salesDashboardV2.table.commission', 'Commissie'), 'P', 'blue')
                },
                {
                    value: SalesColumn.VAT,
                    label: visibilityItem(t('salesDashboardV2.table.vat', 'BTW'), 'P', 'blue')
                }
            ]
        },
        {
            groupLabel: t('salesDashboardV2.table.advertising', 'Adverteren'),
            items: [
                {
                    value: SalesColumn.AD_SPENT,
                    label: visibilityItem(t('salesDashboardV2.table.adCosts', 'Advertentie kosten'), 'A', 'purple')
                },
                {
                    value: SalesColumn.SPONSORED_SALES,
                    label: visibilityItem(t('salesDashboardV2.table.adRevenue', 'Advertentie omzet'), 'A', 'purple')
                },
                {
                    value: SalesColumn.ACOS,
                    label: visibilityItem('ACoS', 'A', 'purple')
                },
                {
                    value: SalesColumn.CPC,
                    label: visibilityItem('CPC', 'A', 'purple')
                }
            ]
        },
        {
            groupLabel: t('salesDashboardV2.table.returns', 'Retouren'),
            items: [
                {
                    value: SalesColumn.RETURN_QUANTITY,
                    label: visibilityItem(t('salesDashboardV2.table.returnQuantity', 'Aantal retour'), 'R', 'red')
                },
                {
                    value: SalesColumn.RETURN_REVENUE,
                    label: visibilityItem(t('salesDashboardV2.table.returnRevenue', 'Retour omzet'), 'R', 'red')
                },
                {
                    value: SalesColumn.RETURN_COSTS,
                    label: visibilityItem(t('salesDashboardV2.table.returnCosts', 'Retour kosten'), 'R', 'red')
                },
                {
                    value: SalesColumn.RETURN_COMMISSION,
                    label: visibilityItem(t('salesDashboardV2.table.returnCommission', 'Retour commissie'), 'R', 'red')
                }
            ]
        },
        {
            groupLabel: t('salesDashboardV2.table.stock', 'Voorraad'),
            items: [
                {
                    value: SalesColumn.STOCK,
                    label: visibilityItem(
                        t('salesDashboardV2.table.stock', 'Voorraad'),
                        // We must use the translated string to determine the first letter, since NL and EN differ in first letter
                        t('salesDashboardV2.table.stock', 'Voorraad').substring(0, 1).toUpperCase(),
                        'yellow'
                    )
                }
            ]
        }
    ];
    const {
        onChangeSearch,
        onEnterSearch,
        onChangeCategoryVisibility,
        onChangeColumnVisibility,
        onChangeCountries,
        onChangeAccounts,
        onChangePeriod,
        onChangeOrder,
        onApplyFilter,
        onChangePage,
        onChangeLimit,
        onClickProduct,
        onEditCosts,
        onExport,
        search,
        countryCodes,
        accountIDs,
        period,
        customPeriodStart,
        customPeriodEnd,
        page,
        limit,
        total,
        order,
        filters,
        products,
        loadingProducts,
        stats,
        comparingStats,
        visibleColumns,
        comparisonVisible,
        onUploadCosts
    } = props;
    const accountItems: Array<PortalSelectOption> = useMemo(
        () =>
            accounts.map((account: Account) => ({
                value: account.id,
                label: account.selectablePlatformLabel
            })),
        [accounts]
    );

    function renderStats() {
        return (
            <div className='stats-container'>
                <BluemateStatCard
                    data={[
                        {
                            label: t('salesDashboardV2.numOrders', 'Aantal orders'),
                            value: stats?.num_orders ?? 0,
                            prevValue: comparingStats?.num_orders ?? 0,
                            useThousandSeparator: true,
                            zeroAsDash: false,
                            hidePercentageToggle: true
                        },
                        {
                            label: t('salesDashboardV2.table.quantitySold', 'Aantal verkocht'),
                            value: stats?.quantity ?? 0,
                            prevValue: comparingStats?.quantity ?? 0,
                            useThousandSeparator: true,
                            zeroAsDash: false,
                            hidePercentageToggle: true
                        }
                    ]}
                    hideComparison={period === PeriodLabel.ALL}
                />
                <BluemateStatCard
                    data={[
                        {
                            label: t('salesDashboardV2.revenueExcl', 'Omzet excl. BTW'),
                            value: stats?.revenue_excl ?? 0,
                            prevValue: comparingStats?.revenue_excl ?? 0,
                            valueType: StatCardDataType.CURRENCY,
                            zeroAsDash: false,
                            hidePercentageToggle: true
                        },
                        {
                            label: t('salesDashboardV2.revenueIncl', 'Omzet incl. BTW'),
                            value: stats?.revenue_incl ?? 0,
                            prevValue: comparingStats?.revenue_incl ?? 0,
                            valueType: StatCardDataType.CURRENCY,
                            zeroAsDash: false,
                            hidePercentageToggle: true
                        }
                    ]}
                    hideComparison={period === PeriodLabel.ALL}
                />
                <BluemateStatCard
                    data={{
                        label: t('salesDashboardV2.profitExcl', 'Winst excl. BTW'),
                        value: stats?.profit ?? null,
                        prevValue: comparingStats?.profit ?? null,
                        valueType: StatCardDataType.CURRENCY,
                        zeroAsDash: true,
                        info: t(
                            'profitInfo',
                            'De winst wordt enkel berekend met producten waarvoor de kosten zijn ingesteld.'
                        ),
                        alternates: [
                            {
                                label: t('salesDashboardV2.margin', 'Marge'),
                                value: stats?.margin ?? null,
                                prevValue: comparingStats?.margin ?? null,
                                valueType: StatCardDataType.PERCENTAGE,
                                zeroAsDash: true,
                                info: t(
                                    'profitInfo',
                                    'De winst wordt enkel berekend met producten waarvoor de kosten zijn ingesteld.'
                                )
                            }
                        ]
                    }}
                    hideComparison={period === PeriodLabel.ALL}
                />
                {renderOrganicAdsStat()}
                {renderReturnsStat()}
            </div>
        );
    }

    function renderOrganicAdsStat() {
        const revenue = stats?.revenue_incl ?? 0;
        const prevRevenue = comparingStats?.revenue_incl ?? 0;
        const organicRevenue = stats?.organic_sales ?? 0;
        const prevOrganicRevenue = comparingStats?.organic_sales ?? 0;
        const organicPercentage = (organicRevenue / (revenue || 1)) * 100;
        const prevOrganicPercentage = (prevOrganicRevenue / (prevRevenue || 1)) * 100;
        const adRevenue = stats?.sponsored_sales ?? 0;
        const prevAdRevenue = comparingStats?.sponsored_sales ?? 0;
        const adPercentage = (adRevenue / (revenue || 1)) * 100;
        const prevAdPercentage = (prevAdRevenue / (prevRevenue || 1)) * 100;
        const data: Array<StatCardData> = [
            {
                label: t('salesDashboardV2.table.organicRevenue', 'Organische omzet'),
                value: stats?.organic_sales ?? 0,
                prevValue: comparingStats?.organic_sales ?? 0,
                valueType: StatCardDataType.CURRENCY,
                zeroAsDash: false,
                alternates: [
                    {
                        label: t('overview.kpi.shareOfTotal', '% van totale omzet'),
                        value: organicPercentage,
                        prevValue: prevOrganicPercentage,
                        valueType: StatCardDataType.PERCENTAGE,
                        zeroAsDash: false
                    }
                ]
            },
            {
                label: t('salesDashboardV2.table.adRevenue', 'Advertentie omzet'),
                value: stats?.sponsored_sales ?? 0,
                prevValue: comparingStats?.sponsored_sales ?? 0,
                valueType: StatCardDataType.CURRENCY,
                zeroAsDash: false,
                alternates: [
                    {
                        label: t('overview.kpi.shareOfTotal', '% van totale omzet'),
                        value: adPercentage,
                        prevValue: prevAdPercentage,
                        valueType: StatCardDataType.PERCENTAGE,
                        zeroAsDash: false
                    }
                ]
            }
        ];
        return <BluemateStatCard data={data} hideComparison={period === PeriodLabel.ALL} />;
    }

    function renderReturnsStat() {
        const orderCount = stats?.num_orders ?? 0;
        const prevOrderCount = comparingStats?.num_orders ?? 0;
        const returnCount = stats?.return_quantity ?? 0;
        const prevReturnCount = comparingStats?.return_quantity ?? 0;
        const returnPercentage = (returnCount / (orderCount || 1)) * 100;
        const prevReturnPercentage = (prevReturnCount / (prevOrderCount || 1)) * 100;
        const revenue = stats?.revenue_excl ?? 0;
        const prevRevenue = comparingStats?.revenue_excl ?? 0;
        const returnRevenue = stats?.returned_revenue_excl ?? 0;
        const prevReturnRevenue = comparingStats?.returned_revenue_excl ?? 0;
        const returnRevenuePercentage = (returnRevenue / (revenue || 1)) * 100;
        const prevReturnRevenuePercentage = (prevReturnRevenue / (prevRevenue || 1)) * 100;
        const data: Array<StatCardData> = [
            {
                label: t('salesDashboardV2.table.returnQuantity', 'Aantal retour'),
                value: stats?.return_quantity ?? 0,
                prevValue: comparingStats?.return_quantity ?? 0,
                invertTrend: true,
                useThousandSeparator: true,
                zeroAsDash: false,
                alternates: [
                    {
                        label: t('overview.kpi.returnRate', 'Retour %'),
                        value: returnPercentage,
                        prevValue: prevReturnPercentage,
                        valueType: StatCardDataType.PERCENTAGE,
                        zeroAsDash: false
                    }
                ]
            },
            {
                label: t('salesDashboardV2.returnRevenueAlt', 'Omzet retour'),
                value: stats?.returned_revenue_excl ?? 0,
                prevValue: comparingStats?.returned_revenue_excl ?? 0,
                valueType: StatCardDataType.CURRENCY,
                invertTrend: true,
                zeroAsDash: false,
                alternates: [
                    {
                        label: t('overview.kpi.shareOfTotal', '% van totale omzet'),
                        value: returnRevenuePercentage,
                        prevValue: prevReturnRevenuePercentage,
                        valueType: StatCardDataType.PERCENTAGE,
                        zeroAsDash: false
                    }
                ]
            }
        ];
        return <BluemateStatCard data={data} hideComparison={period === PeriodLabel.ALL} />;
    }

    function renderGlobalFilters() {
        return (
            <div className='global-filters'>
                <div className='control-bar'>
                    <div className='control-bar-row'>
                        {renderSearch()}
                        {renderSelectables()}
                    </div>
                    {renderVisibilityToggles()}
                </div>
            </div>
        );
    }

    function renderSearch() {
        return (
            <BluemateSearchInput
                onChange={onChangeSearch}
                onEnter={onEnterSearch}
                value={search}
                placeholder={t('salesDashboardV2.searchPlaceholder', 'Zoek producttitel of EAN...')}
                className='search-field'
                size='small'
                autoFocus
            />
        );
    }

    function renderSelectables() {
        return (
            <div className='selectable-filters'>
                <PortalSelectController
                    onChange={onChangeCountries}
                    items={countryItems}
                    shape='rounded'
                    value={countryCodes}
                    size='small'
                    label={t('salesDashboardV2.salesFrom', 'Sales from')}
                />
                <PortalSelectController
                    onChange={onChangeAccounts}
                    items={accountItems}
                    value={Array.from(accountIDs)}
                    shape='rounded'
                    size='small'
                    controls={['selectAll', 'deselectAll']}
                    multiple
                    label={t('salesDashboardV2.shops', 'Winkels')}
                    parseSelectValue={(values: Array<string>) => {
                        if (values.length === 0 || values.length === accountItems.length)
                            return t('salesDashboardV2.allShops', 'Alle winkels');
                        if (values.length === 1) return t('salesDashboardV2.oneShop', '1 winkel');
                        return t('salesDashboardV2.nShops', '{{count}} winkels', {count: values.length});
                    }}
                />
                <BluematePeriodSelector
                    onChange={onChangePeriod}
                    value={[customPeriodStart, customPeriodEnd]}
                    selectedPreset={period}
                    selectSize='small'
                    selectShape='rounded'
                    label={t('salesDashboardV2.period', 'Periode')}
                    itemsMaxHeight='25rem'
                />
                <PortalSelectController
                    onChange={onChangeColumnVisibility}
                    items={columnVisibilityItems}
                    itemsHeight='30rem'
                    shape='rounded'
                    size='small'
                    multiple
                    disableAutoScroll
                    label={t('salesDashboardV2.columns', 'Kolommen')}
                    value={columnVisibilityItems.reduce(
                        (prev: Array<SalesColumn>, item) =>
                            prev.concat(
                                item.items.filter(item => visibleColumns.has(item.value)).map(item => item.value)
                            ),
                        []
                    )}
                    parseSelectValue={(values: Array<SalesColumn>) => {
                        if (values.length === 1) return t('salesDashboardV2.oneColumn', '1 kolom');
                        return t('salesDashboardV2.nColumns', '{{count}} kolommen', {count: values.length});
                    }}
                    controls={['selectAll', 'deselectAll']}
                />
            </div>
        );
    }

    function renderVisibilityToggles() {
        return (
            <div className='visibility-toggles'>
                {renderCategoryVisibilityToggle(
                    t('salesDashboardV2.table.sales', 'Verkopen'),
                    SalesCategory.SALES,
                    'green'
                )}
                {renderCategoryVisibilityToggle(
                    t('salesDashboardV2.table.profit', 'Winst'),
                    SalesCategory.PROFIT,
                    'orange'
                )}
                {renderCategoryVisibilityToggle(
                    t('salesDashboardV2.table.costs', 'Kosten'),
                    SalesCategory.COSTS,
                    'blue'
                )}
                {renderCategoryVisibilityToggle(
                    t('salesDashboardV2.table.advertising', 'Adverteren'),
                    SalesCategory.ADVERTISING,
                    'purple'
                )}
                {renderCategoryVisibilityToggle(
                    t('salesDashboardV2.table.returns', 'Retouren'),
                    SalesCategory.RETURNS,
                    'red'
                )}
                {renderCategoryVisibilityToggle(
                    t('salesDashboardV2.table.stock', 'Voorraad'),
                    SalesCategory.STOCK,
                    'yellow'
                )}
                <div className='separator' />
                {renderCategoryVisibilityToggle(
                    t('salesDashboardV2.comparison', 'Vergelijking'),
                    SalesCategory.COMPARISONS,
                    'blue'
                )}
                {renderExport()}
                <BluemateButton
                    onClick={onUploadCosts}
                    label={t('salesDashboardV2.uploadProductCosts', 'Productkosten importeren')}
                    ornaments={{element: <AwesomeIcon name='file-upload' />}}
                    color='yellow'
                    size='small'
                    shape='rounded'
                    secondary
                />
            </div>
        );
    }

    function renderExport() {
        return (
            <div className='export-container'>
                <BluemateButton
                    onClick={() => {}}
                    label={t('salesDashboardV2.export', 'Export')}
                    ornaments={{element: <AwesomeIcon name='file-export' />}}
                    color='green'
                    size='small'
                    shape='rounded'
                    secondary
                />
                <div className='export-options'>
                    <div className='export-option' onClick={onExport('excel')}>
                        <AwesomeIcon name='file-excel' prefix='far' />
                        <span className='export-type'>Excel</span>
                    </div>
                    <div className='export-option' onClick={onExport('csv')}>
                        <AwesomeIcon name='file-csv' />
                        <span className='export-type'>CSV</span>
                    </div>
                </div>
            </div>
        );
    }

    function renderCategoryVisibilityToggle(label: string, category: SalesCategory, color: BluemateControlColor) {
        return (
            <BluemateToggleButton
                label={label}
                color={color}
                onToggle={onChangeCategoryVisibility(category)}
                defaultActive={true}
                size='small'
                shape='rounded'
                ornaments={{element: <AwesomeIcon name='eye' prefix='far' />}}
                secondary
            />
        );
    }

    return (
        <div className='sales-dashboard-v2'>
            {renderStats()}
            {renderGlobalFilters()}
            <SalesDashboardTable
                onChangeOrder={onChangeOrder}
                onApplyFilters={onApplyFilter}
                onClickProduct={onClickProduct}
                onEditCosts={onEditCosts}
                onChangeLimit={onChangeLimit}
                onChangePage={onChangePage}
                total={total}
                products={products}
                order={order}
                loadingProducts={loadingProducts}
                filters={filters}
                limit={limit}
                page={page}
                visibleColumns={visibleColumns}
                comparisonVisible={comparisonVisible}
            />
        </div>
    );
}
